import { MAX_CUSTOM_CLAIMS_BYTES, ROLE_TO_SHORT_CODE, SHORT_CODE_TO_PERSONA, mapContractRoleToPersona, normalizeRoleName, } from './guards.js';
function isValidContractOrLegacyRole(val) {
    return (val === 'SUPER_ADMIN' ||
        val === 'ORG_ADMIN' ||
        val === 'WORKSPACE_MANAGER' ||
        val === 'WORKSPACE_EDITOR' ||
        val === 'CAMPAIGN_EDITOR' ||
        val === 'COMPLIANCE_OFFICER' ||
        val === 'WORKSPACE_VIEWER' ||
        val === 'VIEWER' ||
        val === 'PARTNER_API');
}
function isValidCompactRoleCode(val) {
    return (val === 'SA' ||
        val === 'OA' ||
        val === 'WM' ||
        val === 'WE' ||
        val === 'CE' ||
        val === 'CO' ||
        val === 'VW');
}
export function buildCallerIdentityFromTokenClaims(uid, rawClaims) {
    const claims = rawClaims;
    const orgId = typeof claims.org === 'string' && claims.org.length > 0
        ? claims.org
        : typeof claims.orgId === 'string' && claims.orgId.length > 0
            ? claims.orgId
            : 'org_default';
    const workspacesMap = {};
    const workspacePersonasMap = {};
    if (typeof claims.ws === 'object' && claims.ws !== null) {
        for (const [wsId, code] of Object.entries(claims.ws)) {
            if (isValidCompactRoleCode(code)) {
                const persona = SHORT_CODE_TO_PERSONA[code];
                workspacePersonasMap[wsId] = persona;
                workspacesMap[wsId] = persona;
            }
        }
    }
    if (typeof claims.workspaces === 'object' && claims.workspaces !== null) {
        for (const [wsId, r] of Object.entries(claims.workspaces)) {
            if (isValidContractOrLegacyRole(r)) {
                const normalized = normalizeRoleName(r);
                workspacesMap[wsId] = normalized;
                workspacePersonasMap[wsId] = mapContractRoleToPersona(normalized);
            }
        }
    }
    const wsKeys = Object.keys(workspacesMap);
    const explicitWorkspaceId = typeof rawClaims['workspaceId'] === 'string' && rawClaims['workspaceId'].length > 0
        ? rawClaims['workspaceId']
        : undefined;
    const activeWorkspaceId = explicitWorkspaceId ?? wsKeys[0] ?? 'ws_default';
    let topRole = 'VIEWER';
    if (claims.role !== undefined && isValidContractOrLegacyRole(claims.role)) {
        topRole = normalizeRoleName(claims.role);
    }
    else if (claims.orgAdmin === true) {
        topRole = 'ORG_ADMIN';
    }
    else if (workspacesMap[activeWorkspaceId] !== undefined) {
        topRole = workspacesMap[activeWorkspaceId];
    }
    const effectiveRole = topRole === 'SUPER_ADMIN' || topRole === 'ORG_ADMIN'
        ? topRole
        : (workspacesMap[activeWorkspaceId] ?? topRole);
    const personaRole = mapContractRoleToPersona(effectiveRole);
    const callerIdentity = {
        uid,
        orgId,
        role: effectiveRole,
        personaRole,
        workspaces: workspacesMap,
        workspacePersonas: workspacePersonasMap,
    };
    return {
        callerIdentity,
        orgId,
        activeWorkspaceId,
        effectiveRole,
        personaRole,
    };
}
export function buildWorkspaceMembershipId(uid, workspaceId) {
    return `${uid}_${workspaceId}`;
}
export function buildOrgAdminMembershipId(uid, orgId) {
    return `${uid}_ORG_${orgId}`;
}
export function buildCompactCustomClaims(input) {
    const hasComplianceOfficerRole = Object.values(input.workspaceRoles).some((r) => normalizeRoleName(r) === 'COMPLIANCE_OFFICER');
    const topRole = input.isSuperAdmin
        ? 'SUPER_ADMIN'
        : input.isOrgAdmin
            ? 'ORG_ADMIN'
            : hasComplianceOfficerRole
                ? 'COMPLIANCE_OFFICER'
                : 'VIEWER';
    const fullWsShort = {};
    const fullWorkspaces = {};
    for (const [wsId, r] of Object.entries(input.workspaceRoles)) {
        fullWsShort[wsId] = ROLE_TO_SHORT_CODE[r];
        fullWorkspaces[wsId] = normalizeRoleName(r);
    }
    const candidate = {
        org: input.orgId,
        orgAdmin: input.isOrgAdmin,
        ws: fullWsShort,
        orgId: input.orgId,
        role: topRole,
        workspaces: fullWorkspaces,
    };
    const fullBytes = Buffer.byteLength(JSON.stringify(candidate), 'utf8');
    if (fullBytes <= MAX_CUSTOM_CLAIMS_BYTES) {
        return { claims: candidate, byteLength: fullBytes, overflowed: false };
    }
    const fittedWsShort = {};
    const fittedWorkspaces = {};
    for (const [wsId, shortCode] of Object.entries(fullWsShort)) {
        const contractRole = fullWorkspaces[wsId];
        if (contractRole === undefined)
            continue;
        fittedWsShort[wsId] = shortCode;
        fittedWorkspaces[wsId] = contractRole;
        const probe = {
            org: input.orgId,
            orgAdmin: input.isOrgAdmin,
            ws: fittedWsShort,
            claimsOverflow: true,
            orgId: input.orgId,
            role: topRole,
            workspaces: fittedWorkspaces,
        };
        if (Buffer.byteLength(JSON.stringify(probe), 'utf8') > MAX_CUSTOM_CLAIMS_BYTES) {
            delete fittedWsShort[wsId];
            delete fittedWorkspaces[wsId];
            break;
        }
    }
    const truncatedClaims = {
        org: input.orgId,
        orgAdmin: input.isOrgAdmin,
        ws: fittedWsShort,
        claimsOverflow: true,
        orgId: input.orgId,
        role: topRole,
        workspaces: fittedWorkspaces,
    };
    return {
        claims: truncatedClaims,
        byteLength: Buffer.byteLength(JSON.stringify(truncatedClaims), 'utf8'),
        overflowed: true,
    };
}
export async function resolveEffectiveRoleWithFallback(uid, targetOrgId, targetWorkspaceId, claims, lookup) {
    if (claims?.role === 'SUPER_ADMIN') {
        return 'SUPER_ADMIN';
    }
    const tokenOrg = claims?.org ?? claims?.orgId;
    if (tokenOrg !== undefined && tokenOrg !== targetOrgId) {
        return null;
    }
    if (claims?.orgAdmin === true || claims?.role === 'ORG_ADMIN') {
        return 'ORG_ADMIN';
    }
    const inlineCode = claims?.ws?.[targetWorkspaceId];
    if (inlineCode !== undefined) {
        return SHORT_CODE_TO_PERSONA[inlineCode];
    }
    const inlineRole = claims?.workspaces?.[targetWorkspaceId];
    if (inlineRole !== undefined) {
        return mapContractRoleToPersona(inlineRole);
    }
    if (lookup.hasOrgAdminMembership !== undefined) {
        const isOrgAdm = await lookup.hasOrgAdminMembership(uid, targetOrgId);
        if (isOrgAdm) {
            return 'ORG_ADMIN';
        }
    }
    const doc = await lookup.getWorkspaceMembership(uid, targetWorkspaceId);
    if (!doc || doc.orgId !== targetOrgId) {
        return null;
    }
    return mapContractRoleToPersona(doc.role);
}
//# sourceMappingURL=claims.js.map